/**
 * Проигрывание ФРАГМЕНТА видео `[start - preroll, end + tail]` — центральное
 * окно игрового экрана (docs/GAME.md, «Экран реплики»).
 *
 * Умеет две вещи:
 *  - проиграть фрагмент со звуком оригинала («послушать перед записью»);
 *  - проиграть фрагмент с записанным дублем поверх — тот самый момент, ради
 *    которого играют.
 *
 * Дубль подмешивается через Web Audio тем же планировщиком, что и превью
 * редактора (audio/clips.ts): source.start(when) даёт точность в единицы
 * миллисекунд, чего от <audio> добиться нельзя.
 *
 * MediaElementSource на <video> НЕ создаётся: он врезается в элемент навсегда,
 * а игровому экрану достаточно `video.muted`. Заодно это исключает конфликт
 * с превью-микшером редактора.
 */

import { ClipScheduler } from '../audio/clips'
import type { Clip } from '../audio/clips'
import { PREROLL_SECONDS, TAIL_SECONDS } from '../audio/recorder'

export type ClipRange = { from: number; to: number }

/** Границы фрагмента реплики. */
export function clipRange(start: number, end: number): ClipRange {
  return { from: Math.max(0, start - PREROLL_SECONDS), to: end + TAIL_SECONDS }
}

export type PlayFragmentOptions = {
  from: number
  to: number
  /** Слышен ли звук самого видео. */
  withOriginal: boolean
  /** Подмешивать ли загруженный дубль. */
  withTake: boolean
}

export class FragmentPlayer {
  private readonly video: HTMLVideoElement
  private readonly scheduler: ClipScheduler
  private ctx: AudioContext | null = null
  private takeClip: Clip | null = null
  private mixing = false
  private token = 0
  private rafId: number | null = null
  private disposed = false

  constructor(video: HTMLVideoElement) {
    this.video = video
    this.scheduler = new ClipScheduler({
      video,
      getContext: () => this.ctx,
      getClips: () => (this.mixing && this.takeClip ? [this.takeClip] : []),
      isActive: () => this.mixing && !this.disposed,
    })
  }

  /**
   * Декодирует записанный дубль. `startAt` — абсолютный таймкод видео, на
   * котором начинается содержимое файла (`line.start + take.offset`).
   * false — браузер не смог разобрать собственную запись; тогда фрагмент
   * проигрывается с оригиналом, а дубль всё равно уже улетел на сервер.
   */
  async loadTake(blob: Blob, startAt: number): Promise<boolean> {
    this.takeClip = null
    if (this.disposed) return false
    try {
      const ctx = this.ensureContext()
      if (ctx.state === 'suspended') await ctx.resume()
      const buffer = await ctx.decodeAudioData(await blob.arrayBuffer())
      if (this.disposed) return false
      this.takeClip = { buffer, startAt, gainDb: 0 }
      return true
    } catch {
      return false
    }
  }

  clearTake(): void {
    this.takeClip = null
    this.mixing = false
    this.scheduler.stopAll()
  }

  hasTake(): boolean {
    return this.takeClip !== null
  }

  /** Поставить кадр без воспроизведения — чтобы экран не был чёрным. */
  async showFrame(at: number): Promise<void> {
    if (this.disposed) return
    this.stop()
    this.video.muted = true
    await this.seek(Math.max(0, at))
  }

  /**
   * Проиграть фрагмент. Промис разрешается на `to`, на конце видео или когда
   * воспроизведение прервали через stop().
   */
  async play(options: PlayFragmentOptions): Promise<void> {
    if (this.disposed) return
    this.stop()
    const token = this.token
    const video = this.video
    const wantTake = options.withTake && this.takeClip !== null

    video.muted = !options.withOriginal
    video.playbackRate = 1
    this.mixing = wantTake

    if (wantTake) {
      const ctx = this.ensureContext()
      if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined)
      if (this.disposed || token !== this.token) return
    }

    await this.seek(Math.max(0, options.from))
    if (this.disposed || token !== this.token) return

    try {
      await video.play()
    } catch {
      // Прерывание через stop() тоже отклоняет промис play() — это не ошибка.
      if (this.disposed || token !== this.token) return
      this.halt()
      throw new Error('Браузер не дал запустить видео. Нажмите на окно и повторите.')
    }
    if (this.disposed || token !== this.token) return
    // Планировщик сам ловит событие play, но между seek и play он мог не успеть.
    this.scheduler.reschedule()

    await new Promise<void>((resolve) => {
      const loop = (): void => {
        this.rafId = null
        if (this.disposed || token !== this.token) {
          resolve()
          return
        }
        if (video.currentTime >= options.to || video.ended || video.paused) {
          resolve()
          return
        }
        this.rafId = window.requestAnimationFrame(loop)
      }
      this.rafId = window.requestAnimationFrame(loop)
    })

    if (this.disposed || token !== this.token) return
    this.halt()
  }

  /** Прервать текущее воспроизведение. Промис play() при этом разрешится. */
  stop(): void {
    this.token += 1
    this.halt()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.token += 1
    this.cancelLoop()
    this.scheduler.dispose()
    this.takeClip = null
    this.mixing = false
    try {
      this.video.pause()
    } catch {
      // элемент мог быть уже отсоединён
    }
    const ctx = this.ctx
    this.ctx = null
    if (ctx && ctx.state !== 'closed') void ctx.close().catch(() => undefined)
  }

  // ------------------------------------------------------------ внутренности

  private halt(): void {
    this.cancelLoop()
    this.mixing = false
    this.scheduler.stopAll()
    if (!this.disposed && !this.video.paused) this.video.pause()
  }

  private cancelLoop(): void {
    if (this.rafId !== null) {
      window.cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  private ensureContext(): AudioContext {
    if (this.ctx) return this.ctx
    const ctx = new AudioContext()
    this.ctx = ctx
    return ctx
  }

  /** Перемотка с подстраховкой: не все браузеры шлют `seeked` без движения. */
  private seek(target: number): Promise<void> {
    const video = this.video
    return new Promise((resolve) => {
      if (Math.abs(video.currentTime - target) < 0.02) {
        resolve()
        return
      }
      let settled = false
      let timer = 0
      const finish = (): void => {
        if (settled) return
        settled = true
        video.removeEventListener('seeked', finish)
        window.clearTimeout(timer)
        resolve()
      }
      video.addEventListener('seeked', finish)
      timer = window.setTimeout(finish, 2000)
      try {
        video.currentTime = target
      } catch {
        finish()
      }
    })
  }
}
